import React, { useEffect, useState } from "react";
import { FaMinus, FaPlus, FaCloudUploadAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import ProductDescriptionTabs from "./ProductDescriptionTabs";
import SEO from "./SEO";
import RelatedProduct from "./home/RelatedProduct";
import ShopDetailSkeleton from "./ShopDetailSkeleton";

const ShopDetail = () => {
  const ids = window.location.pathname.split("/").pop();
  const navigate = useNavigate();
  const pathParts = window.location.pathname.split("/");
  const { refreshCart } = useCart();
  const id = pathParts.pop();
  const categoryId = pathParts[3];

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [width, setWidth] = useState(1);
  const [height, setHeight] = useState(1);
  const [qty, setQty] = useState(25);

  const [previewImage, setPreviewImage] = useState(null);
  const [fileObject, setFileObject] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({});

  const [showCartPopup, setShowCartPopup] = useState(false);
  const [closingCartPopup, setClosingCartPopup] = useState(false);

  const [inputs, setInputs] = useState({
    border_thread_color: "",
    special_notes: "",
  });

  const getCleanFilterLabel = (label = "") => {
    return String(label).replace(/^select\s+/i, "").trim();
  };

  const getFilterTitle = (label = "") => {
    const cleanLabel = getCleanFilterLabel(label);
    return cleanLabel ? `Select ${cleanLabel}` : "Select Option";
  };

  const getSelectedFiltersPrice = (filterbars, selectedFilters) => {
    let total = 0;

    (filterbars || []).forEach((bar) => {
      const selectedValue = selectedFilters[bar.label];
      if (!selectedValue) return;

      const option = bar.options?.find((opt) => opt.name === selectedValue);

      if (option?.price) {
        total += Number(option.price);
      }
    });

    return total;
  };

  useEffect(() => {
    fetch(`${apiUrl}/api/products/${id}`)
      .then((res) => res.json())
      .then((res) => {
        const p = res.data;
        setProduct(p);

        if (p?.image) {
          setActiveImage(`${apiUrl}/storage/${p.image}`);
        }

        setWidth(Number(p?.filter?.width_min || 1));
        setHeight(Number(p?.filter?.height_min || 1));

        if (p?.filter?.filterbars) {
          const defaults = {};

          p.filter.filterbars.forEach((bar) => {
            if (bar.options && bar.options.length > 0) {
              defaults[bar.label] = bar.options[0].name;
            }
          });

          setSelectedFilters(defaults);
        }
      })
      .catch((err) => console.log(err));
  }, [id]);

  if (!product) return <ShopDetailSkeleton />;

  const p = product;
  const filter = p?.filter || {};

  const getSizeAvg = (w, h) => (w + h) / 2;

  const getBasePrice = (rules, w, h) => {
    const size = getSizeAvg(w, h);
    for (let r of rules || []) {
      if (size >= parseFloat(r.min) && size <= parseFloat(r.max)) {
        return parseFloat(r.price);
      }
    }
    return 0;
  };

  const getDiscountPercent = (discounts, qty) => {
    let discount = 0;
    (discounts || []).forEach((d) => {
      if (Number(qty) >= Number(d.qty)) discount = Number(d.discount);
    });
    return discount;
  };

  const basePrice = getBasePrice(filter.rules, width, height);

  const selectedFiltersPrice = getSelectedFiltersPrice(
    filter.filterbars,
    selectedFilters
  );

  const priceBeforeDiscount = basePrice + selectedFiltersPrice;
  const discountPercent = getDiscountPercent(filter.discounts, qty);

  const finalUnitPrice =
    priceBeforeDiscount - (priceBeforeDiscount * discountPercent) / 100;

  const totalPrice = finalUnitPrice * Number(qty || 0);

  const activeDiscountRule = (filter.discounts || []).reduce((active, d) => {
    return Number(qty) >= Number(d.qty) ? d : active;
  }, null);

  const activeDiscountQty = activeDiscountRule
    ? Number(activeDiscountRule.qty)
    : null;

  const getUserOrGuestId = () => {
    const userData = localStorage.getItem("user");
    console.log(userData, "userData");

    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user?.id) return { user_id: user.id, guest_id: null };
      } catch (err) {
        console.error("Invalid user format in localStorage");
      }
    }

    let guestId = localStorage.getItem("guest_id");
    if (!guestId) {
      guestId = "guest_" + Math.random().toString(36).substring(2, 12);
      localStorage.setItem("guest_id", guestId);
    }

    return { user_id: null, guest_id: guestId };
  };

  const closeCartPopup = () => {
    setClosingCartPopup(true);

    setTimeout(() => {
      setShowCartPopup(false);
      setClosingCartPopup(false);
    }, 260);
  };

  const addToCart = async (showPopup = true) => {
    const formData = new FormData();

    formData.append("product_id", p.id);
    formData.append("product_name", p.name);
    formData.append("qty", qty);
    formData.append("price", totalPrice.toFixed(2));

    if (filter?.price_rules_enabled) {
      formData.append("width", width);
      formData.append("height", height);
    }

    formData.append("selected_filters", JSON.stringify(selectedFilters));
    formData.append("inputs", JSON.stringify(inputs));

    if (fileObject) {
      formData.append("upload_image", fileObject);
    }

    const { user_id, guest_id } = getUserOrGuestId();
    if (user_id) formData.append("user_id", user_id);
    if (guest_id) formData.append("guest_id", guest_id);

    try {
      const res = await fetch(`${apiUrl}/api/cart/add`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        refreshCart();

        if (showPopup) {
          setClosingCartPopup(false);
          setShowCartPopup(true);
        }

        return true;
      } else {
        alert("Something went wrong.");
        return false;
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      return false;
    }
  };

  const handleQtyChange = (value) => {
    const val = Number(value);
    setQty(val < 25 ? 25 : val);
  };

  const buyNow = async () => {
    const success = await addToCart(false);
    if (success) navigate("/checkout");
  };

  return (
    <>
      <style>
        {`
          @keyframes cartToastIn {
            from {
              opacity: 0;
              transform: translateX(24px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
          }

          @keyframes cartToastOut {
            from {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
            to {
              opacity: 0;
              transform: translateX(24px) scale(0.98);
            }
          }
        `}
      </style>

      <SEO
        title={product.meta_title || product.name}
        description={
          product.meta_description ||
          product.otherDescription?.replace(/<[^>]+>/g, "").slice(0, 160)
        }
        keywords={product.meta_keywords || product.name}
      />

      <div className="w-full bg-lightPastelPurple px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            {/* LEFT IMAGE SECTION */}
            <div className="lg:sticky lg:top-24 self-start">
              <div className="relative overflow-hidden rounded-[34px] border border-pastelPurple bg-white p-4 shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:p-5">
                <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
                <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

                <div className="relative flex min-h-[330px] items-center justify-center rounded-[26px] bg-gradient-to-br from-[#f2ecff] via-white to-[#f8ffe0] p-4 md:min-h-[480px]">
                  <img
                    src={activeImage || ""}
                    alt={p?.name || ""}
                    className="max-h-[440px] w-full rounded-2xl object-contain drop-shadow-[0_18px_35px_rgba(47,34,84,0.15)]"
                  />
                </div>
              </div>

              {/* GALLERY */}
              <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-5 xl:grid-cols-7">
                {[
                  ...(p?.image ? [`${apiUrl}/storage/${p.image}`] : []),
                  ...(p?.galleryImages?.map((img) => `${apiUrl}/storage/${img}`) ||
                    []),
                ]
                  .slice(0, 7)
                  .map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImage(img)}
                      className={`aspect-square overflow-hidden rounded-2xl border bg-white p-1.5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 ${
                        activeImage === img
                          ? "border-themePurple ring-4 ring-pastelPurple/70"
                          : "border-pastelPurple hover:border-themePurple"
                      }`}
                    >
                      <img
                        src={img}
                        className="h-full w-full rounded-xl object-contain"
                        alt=""
                      />
                    </button>
                  ))}
              </div>
            </div>

            {/* RIGHT SECTION */}
            <div className="lg:sticky lg:top-24 self-start">
              <div className="relative overflow-hidden rounded-[34px] border border-pastelPurple bg-white p-6 shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:p-8">
                <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-keyLime/20 blur-3xl"></div>
                <div className="absolute -left-24 -bottom-24 h-80 w-80 rounded-full bg-pastelPurple/60 blur-3xl"></div>

                <div className="relative">
                  <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-4xl">
                    {p?.name}
                  </h1>

                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                    <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                  </div>

                  <p className="mt-5 text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                    {p?.description_paragraph}
                  </p>

                  {/* FILTER BARS */}
                  {/* FILTER BARS */}
{filter?.filterbars?.map((bar) => (
  <div className="mt-6" key={bar.label}>
    <h2 className="text-sm font-extrabold capitalize text-darkPurple sm:text-base">
      {getFilterTitle(bar.label)}:{" "}
      <span className="text-red-500">*</span>
    </h2>

    <p className="mt-2 text-base font-extrabold text-[#5f7f00] sm:text-lg">
      {selectedFilters[bar.label]}
    </p>

    <div className="mt-4 flex flex-wrap gap-2.5 sm:gap-3">
  {bar.options?.map((opt, i) => {
    const isActive = selectedFilters[bar.label] === opt.name;

    return (
      <button
        key={i}
        type="button"
        title={opt.name}
        onClick={() =>
          setSelectedFilters({
            ...selectedFilters,
            [bar.label]: opt.name,
          })
        }
        className={`group relative flex h-[70px] w-[70px] flex-col items-center justify-center overflow-hidden rounded-[10px] border p-1.5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#211b33] hover:bg-[#eef3ef] hover:shadow-[0_10px_22px_rgba(47,34,84,0.12)] sm:h-[76px] sm:w-[76px] md:h-[82px] md:w-[82px] ${
          isActive
            ? "border-[#211b33] bg-[#eef3ef] shadow-[0_10px_22px_rgba(47,34,84,0.12)] ring-1 ring-[#211b33]/10"
            : "border-[#d8d4df] bg-[#f4f0ff]"
        }`}
      >
        <div className="flex h-[46px] w-[46px] items-center justify-center overflow-hidden rounded-[8px] transition-all duration-300 sm:h-[52px] sm:w-[52px] md:h-[56px] md:w-[56px]">
          {opt.image ? (
            <img
              src={`${apiUrl}/storage/${opt.image}`}
              className="h-full w-full object-contain transition duration-300 group-hover:scale-105"
              alt={opt.name}
            />
          ) : (
            <span className="px-1 text-center text-[10px] font-extrabold leading-3 text-darkPurple">
              {opt.name}
            </span>
          )}
        </div>

        <span className="mt-1 line-clamp-1 max-w-full px-1 text-center text-[10px] font-medium leading-3 text-[#262b31] sm:text-[11px]">
          {opt.name}
        </span>
      </button>
    );
  })}
</div>
  </div>
))}

                  {/* BORDER THREAD COLOR */}
                  {filter?.border_thread_color && (
                    <div className="mt-7">
                      <label className="text-sm font-extrabold text-darkPurple">
                        Border Thread Color
                      </label>
                      <input
                        type="text"
                        className="mt-2 w-full rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-3.5 text-base font-medium text-darkPurple outline-none transition placeholder:text-[#8a829a] focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60"
                        placeholder="Enter border thread color"
                        onChange={(e) =>
                          setInputs({
                            ...inputs,
                            border_thread_color: e.target.value,
                          })
                        }
                      />
                    </div>
                  )}

                  {/* SPECIAL NOTES */}
                  {filter?.special_notes && (
                    <div className="mt-7">
                      <label className="text-sm font-extrabold text-darkPurple">
                        Special Notes
                      </label>
                      <textarea
                        className="mt-2 w-full resize-none rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-3.5 text-base font-medium text-darkPurple outline-none transition placeholder:text-[#8a829a] focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60"
                        rows="4"
                        placeholder="Add your special notes..."
                        onChange={(e) =>
                          setInputs({ ...inputs, special_notes: e.target.value })
                        }
                      />
                    </div>
                  )}

                  {/* UPLOAD IMAGE */}
                  {filter?.upload_image && (
                    <div className="mt-8 overflow-hidden rounded-[28px] border border-pastelPurple bg-lightPastelPurple p-5 text-center shadow-sm">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-pastelPurple bg-white shadow-sm">
                        <FaCloudUploadAlt className="text-4xl text-themePurple" />
                      </div>

                      <label
                        htmlFor="uploadImage"
                        className="inline-flex cursor-pointer rounded-2xl bg-keyLime px-7 py-3 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
                      >
                        Upload Image
                      </label>

                      <input
                        id="uploadImage"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          setFileObject(file);
                          if (file) setPreviewImage(URL.createObjectURL(file));
                        }}
                      />

                      {previewImage && (
                        <div className="mt-6">
                          <img
                            src={previewImage}
                            className="mx-auto h-44 w-44 rounded-2xl border border-pastelPurple bg-white object-cover shadow-lg"
                            alt=""
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* WIDTH & HEIGHT */}
                  {filter?.price_rules_enabled && (
                    <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div className="rounded-[24px] border border-pastelPurple bg-lightPastelPurple p-4">
                        <label className="mb-3 block text-sm font-extrabold text-darkPurple">
                          Width (Inches):
                        </label>

                        <div className="flex w-full items-center justify-between rounded-2xl border border-pastelPurple bg-white px-4 py-3">
                          <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                            onClick={() =>
                              setWidth(Math.max(filter.width_min || 1, width - 0.5))
                            }
                          >
                            <FaMinus />
                          </button>

                          <span className="text-lg font-extrabold text-darkPurple">
                            {width}
                          </span>

                          <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                            onClick={() =>
                              setWidth(Math.min(filter.width_max || 100, width + 0.5))
                            }
                          >
                            <FaPlus />
                          </button>
                        </div>

                        <span className="mt-2 block text-sm font-medium text-[#5f5870]">
                          Min - {filter.width_min}, Max {filter.width_max}
                        </span>
                      </div>

                      <div className="rounded-[24px] border border-pastelPurple bg-lightPastelPurple p-4">
                        <label className="mb-3 block text-sm font-extrabold text-darkPurple">
                          Height (Inches):
                        </label>

                        <div className="flex w-full items-center justify-between rounded-2xl border border-pastelPurple bg-white px-4 py-3">
                          <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                            onClick={() =>
                              setHeight(
                                Math.max(filter.height_min || 1, height - 0.5)
                              )
                            }
                          >
                            <FaMinus />
                          </button>

                          <span className="text-lg font-extrabold text-darkPurple">
                            {height}
                          </span>

                          <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                            onClick={() =>
                              setHeight(
                                Math.min(filter.height_max || 100, height + 0.5)
                              )
                            }
                          >
                            <FaPlus />
                          </button>
                        </div>

                        <span className="mt-2 block text-sm font-medium text-[#5f5870]">
                          Min - {filter.height_min}, Max - {filter.height_max}
                        </span>
                      </div>
                    </div>
                  )}

                  {filter?.discounts && (
                    <div className="mt-7 overflow-hidden rounded-[24px] border border-pastelPurple bg-white">
                      <table className="w-full text-left text-sm text-[#5f5870]">
                        <thead className="bg-darkPurple text-xs font-extrabold uppercase text-white">
                          <tr>
                            <th className="px-4 py-3">Qty</th>
                            <th className="px-4 py-3">Discount (%)</th>
                            <th className="px-4 py-3">Price After Discount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filter.discounts.map((d, i) => {
                            const discountedPrice =
                              priceBeforeDiscount -
                              (priceBeforeDiscount * Number(d.discount)) / 100;

                            const isActive =
                              Number(d.qty) === Number(activeDiscountQty);

                            return (
                              <tr
                                key={i}
                                onClick={() => handleQtyChange(d.qty)}
                                className={`cursor-pointer border-b border-pastelPurple transition-all duration-300 last:border-b-0 hover:bg-lightPastelPurple ${
                                  isActive
                                    ? "bg-[#f8ffe0] ring-1 ring-inset ring-keyLime"
                                    : "bg-white"
                                }`}
                              >
                                <td className="px-4 py-3 font-bold text-darkPurple">
                                  {d.qty}
                                </td>
                                <td className="px-4 py-3">{d.discount}%</td>
                                <td className="px-4 py-3 font-extrabold text-[#5f7f00]">
                                  ${discountedPrice.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* QUANTITY + PRICE */}
                  <div className="mt-8 grid gap-5 rounded-[28px] border border-pastelPurple bg-lightPastelPurple p-5 md:grid-cols-[0.9fr_1.1fr] md:items-center">
                    <div>
                      <label className="mb-3 block text-sm font-extrabold text-darkPurple">
                      Quantity
                      </label>

                      <div className="flex w-full max-w-[220px] items-center justify-between rounded-2xl border border-pastelPurple bg-white px-3 py-2">
                        <button
                          type="button"
                          className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                          onClick={() => setQty(Math.max(25, Number(qty) - 1))}
                        >
                          <FaMinus />
                        </button>

                        <input
                          type="number"
                          min="25"
                          value={qty}
                          onChange={(e) => setQty(e.target.value)}
                          onBlur={(e) => {
                            const val = Number(e.target.value);
                            if (val < 25 || isNaN(val)) {
                              setQty(25);
                            } else {
                              setQty(val);
                            }
                          }}
                          className="w-16 bg-transparent text-center text-lg font-extrabold text-darkPurple outline-none"
                        />

                        <button
                          type="button"
                          className="flex h-9 w-9 items-center justify-center rounded-xl bg-lightPastelPurple text-darkPurple transition hover:bg-keyLime"
                          onClick={() => setQty(Number(qty) + 1)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-white p-4 text-left md:text-right">
                      <p className="text-sm font-medium text-[#5f5870]">
                        Base Price:{" "}
                        <span className="font-extrabold text-[#5f7f00]">
                          ${(basePrice + selectedFiltersPrice).toFixed(2)}
                        </span>
                      </p>

                      <p className="mt-2 text-base font-bold text-darkPurple">
                        {`$${finalUnitPrice.toFixed(2)} × ${qty}`}
                        <span className="font-extrabold text-[#5f7f00]">
                          {" = $" + totalPrice.toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <button
                      className="rounded-2xl bg-keyLime px-7 py-3.5 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
                      onClick={() => addToCart(true)}
                    >
                      Add to Cart
                    </button>

                    <button
                      className="rounded-2xl border border-pastelPurple bg-white px-7 py-3.5 text-sm font-extrabold text-darkPurple transition-all duration-300 hover:-translate-y-0.5 hover:bg-lightPastelPurple"
                      onClick={buyNow}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
                          
          <ProductDescriptionTabs
            otherDescription={p?.otherDescription}
            additionalInformation={p?.additionalInformation}
          />
        
          <RelatedProduct categoryId={categoryId} />
        </div>
      </div>
                          
      {showCartPopup && (
        <div
          className="fixed right-3 top-20 z-[999] w-[calc(100%-24px)] max-w-[478px] sm:right-5 sm:top-24"
          style={{
            animation: closingCartPopup
              ? "cartToastOut 0.26s ease both"
              : "cartToastIn 0.3s ease both",
          }}
        >
          <div className="relative overflow-hidden rounded-[10px] border border-pastelPurple bg-white px-6 py-6 shadow-[0_14px_40px_rgba(33,27,51,0.16)]">
            <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-pastelPurple/55 blur-3xl"></div>
            <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-keyLime/25 blur-3xl"></div>

            <button
              type="button"
              onClick={closeCartPopup}
              className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#e7ddff] bg-white text-2xl leading-none text-darkPurple transition hover:bg-lightPastelPurple"
              aria-label="Close"
            >
              ×
            </button>

            <div className="relative pr-16">
              <h2 className="text-[22px] font-extrabold leading-tight text-darkPurple sm:text-2xl">
                Product Added To Cart
              </h2>

              <p className="mt-3 line-clamp-1 text-xl font-medium leading-tight text-[#6f6878] sm:text-[22px]">
                {p?.name}
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/cart")}
                  className="rounded-[16px] bg-darkPurple px-7 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-themePurple"
                >
                  View Cart
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/products")}
                  className="text-lg font-extrabold text-darkPurple transition hover:text-themePurple sm:text-xl"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default ShopDetail;



